import React, { useState } from "react";
import {
  ShieldCheckIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import Button from "../componentLibrary/Button";
import InfoCard from "../componentLibrary/InfoCard";
import Input from "../componentLibrary/Input";
import Modal from "../componentLibrary/Modal";
import {
  useSendCurrentEmailVerificationCode,
  useVerifyCurrentEmail,
  useSendEmailChangeVerificationCode,
  useVerifyAndChangeEmail,
} from "../api/mutations";
import { useNotification } from "../hooks/useNotification";

interface ChangeEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type Step =
  | "verify-current-email"
  | "enter-code-current"
  | "enter-new-email"
  | "verify-new-email";

const ChangeEmailModal: React.FC<ChangeEmailModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { notify } = useNotification();

  const [step, setStep] = useState<Step>("verify-current-email");
  const [currentEmail, setCurrentEmail] = useState("");
  const [currentEmailCode, setCurrentEmailCode] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newEmailCode, setNewEmailCode] = useState("");

  const sendCurrentEmailCodeMutation = useSendCurrentEmailVerificationCode({
    onSuccess: async (data: unknown) => {
      const response = data as { message: string; email: string };
      setCurrentEmail(response.email); // Store the current email from response
      notify("success", `Verification code sent to ${response.email}`);
      setStep("enter-code-current");
    },
    onError: (error: unknown) => {
      const err = error as Error;
      notify("error", err?.message || "Failed to send verification code");
    },
  });

  const verifyCurrentEmailMutation = useVerifyCurrentEmail({
    onSuccess: async () => {
      notify("success", "Current email verified!");
      setStep("enter-new-email");
    },
    onError: (error: unknown) => {
      const err = error as Error;
      notify("error", err?.message || "Invalid verification code");
    },
  });

  const sendNewEmailCodeMutation = useSendEmailChangeVerificationCode({
    onSuccess: async (data: unknown) => {
      const response = data as { message: string; newEmail: string };
      notify("success", `Verification code sent to ${response.newEmail}`);
      setStep("verify-new-email");
    },
    onError: (error: unknown) => {
      const err = error as Error;
      notify("error", err?.message || "Failed to send verification code");
    },
  });

  const verifyAndChangeMutation = useVerifyAndChangeEmail({
    onSuccess: async () => {
      notify("success", "Email changed successfully!");
      handleClose();
      if (onSuccess) onSuccess();
    },
    onError: (error: unknown) => {
      const err = error as Error;
      notify("error", err?.message || "Invalid verification code");
    },
  });

  const handleClose = () => {
    setStep("verify-current-email");
    setCurrentEmailCode("");
    setNewEmail("");
    setNewEmailCode("");
    onClose();
  };

  const handleSendCodeToCurrentEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await sendCurrentEmailCodeMutation.mutateAsync();
    } catch (error) {
      // Error is already handled in onError callback
    }
  };

  const handleVerifyCurrentEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentEmailCode.trim()) {
      notify("error", "Please enter the verification code");
      return;
    }

    if (currentEmailCode.length !== 6) {
      notify("error", "Verification code must be 6 digits");
      return;
    }

    try {
      await verifyCurrentEmailMutation.mutateAsync(currentEmailCode.trim());
    } catch (error) {
      // Error is already handled in onError callback
    }
  };

  const handleSendCodeToNewEmail = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newEmail.trim()) {
      notify("error", "Please enter a new email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      notify("error", "Please enter a valid email address");
      return;
    }

    try {
      await sendNewEmailCodeMutation.mutateAsync(newEmail.trim());
    } catch (error) {
      // Error is already handled in onError callback
    }
  };

  const handleVerifyNewEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmailCode.trim()) {
      notify("error", "Please enter the verification code");
      return;
    }

    if (newEmailCode.length !== 6) {
      notify("error", "Verification code must be 6 digits");
      return;
    }

    try {
      await verifyAndChangeMutation.mutateAsync({
        verificationCode: newEmailCode.trim(),
        newEmail: newEmail.trim(),
      });
    } catch (error) {
      // Error is already handled in onError callback
    }
  };

  const handleBack = () => {
    if (step === "enter-code-current") {
      setStep("verify-current-email");
      setCurrentEmailCode("");
    } else if (step === "verify-new-email") {
      setStep("enter-new-email");
      setNewEmailCode("");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onCloseModal={handleClose}
      title="Change Email Address"
    >
      <div className="space-y-6">
        {/* Step 1: Verify Current Email - Send Code */}
        {step === "verify-current-email" && (
          <div className="space-y-4">
            <InfoCard
              variant="info"
              icon={<ShieldCheckIcon className="w-5 h-5 text-electrum" />}
              title="Verify Your Identity"
            >
              <p>
                For security, we&apos;ll send a verification code to your
                current email address{" "}
                {currentEmail && (
                  <strong className="font-semibold">{currentEmail}</strong>
                )}{" "}
                to confirm it&apos;s you.
              </p>
            </InfoCard>

            <form onSubmit={handleSendCodeToCurrentEmail}>
              <div className="flex flex-col xl:flex-row justify-end gap-3 mt-6">
                <Button
                  onClick={handleClose}
                  variant="secondary"
                  className="w-full lg:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={sendCurrentEmailCodeMutation.isPending}
                  className="w-full lg:w-auto"
                >
                  Send Verification Code
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Step 2: Enter Code for Current Email */}
        {step === "enter-code-current" && (
          <div className="space-y-4">
            <InfoCard
              variant="success"
              icon={<EnvelopeIcon className="w-5 h-5 text-green-600" />}
              title="Code Sent to Current Email"
            >
              <p className="text-sm text-green-800 mb-2">
                We&apos;ve sent a 6-digit code to{" "}
                <strong className="font-semibold">{currentEmail}</strong>
              </p>
              <p>
                Please check your inbox and enter the code below. The code will
                expire in 10 minutes.
              </p>
            </InfoCard>

            <form onSubmit={handleVerifyCurrentEmail}>
              <Input
                label="Verification Code"
                type="text"
                value={currentEmailCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setCurrentEmailCode(value);
                }}
                placeholder="Enter 6-digit code"
                maxLength={6}
                required
              />

              <div className="flex flex-col xl:flex-row justify-end gap-3 mt-6">
                <Button
                  onClick={handleBack}
                  variant="secondary"
                  className="w-full xl:w-auto"
                >
                  Back
                </Button>
                <div className="flex flex-col xl:flex-row flex-wrap gap-3 w-full xl:w-auto">
                  <Button
                    onClick={handleClose}
                    variant="secondary"
                    className="w-full xl:w-auto"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={verifyCurrentEmailMutation.isPending}
                    disabled={currentEmailCode.length !== 6}
                    className="w-full xl:w-auto"
                  >
                    Verify Code
                  </Button>
                </div>
              </div>
            </form>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setStep("verify-current-email");
                  setCurrentEmailCode("");
                }}
                className="text-sm text-electrum hover:text-electrum-400 underline"
              >
                Didn&apos;t receive the code? Resend
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Enter New Email */}
        {step === "enter-new-email" && (
          <div className="space-y-4">
            <InfoCard
              variant="info"
              icon={<EnvelopeIcon className="w-5 h-5 text-electrum" />}
              title="Enter Your New Email"
            >
              <p>
                Enter your new email address. We&apos;ll send a verification
                code to that address to confirm you own it.
              </p>
            </InfoCard>

            <form onSubmit={handleSendCodeToNewEmail}>
              <Input
                label="New Email Address"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter your new email address"
                required
              />

              <div className="flex flex-col xl:flex-row justify-end gap-3 mt-6">
                <Button
                  onClick={handleClose}
                  variant="secondary"
                  className="w-full xl:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={sendNewEmailCodeMutation.isPending}
                  disabled={!newEmail.trim()}
                  className="w-full xl:w-auto"
                >
                  Send Verification Code
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Step 4: Verify New Email */}
        {step === "verify-new-email" && (
          <div className="space-y-4">
            <InfoCard
              variant="success"
              icon={<EnvelopeIcon className="w-5 h-5 text-green-600" />}
              title="Code Sent to New Email"
            >
              <p className="text-sm text-green-800 mb-2">
                We&apos;ve sent a 6-digit code to{" "}
                <strong className="font-semibold">{newEmail}</strong>
              </p>
              <p>
                Please check your inbox and enter the code below. The code will
                expire in 10 minutes.
              </p>
            </InfoCard>

            <form onSubmit={handleVerifyNewEmail}>
              <Input
                label="Verification Code"
                type="text"
                value={newEmailCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setNewEmailCode(value);
                }}
                placeholder="Enter 6-digit code"
                maxLength={6}
                required
              />

              <InfoCard
                variant="warning"
                icon={
                  <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" />
                }
                className="mt-4"
              >
                <p className="text-sm text-yellow-800">
                  <strong className="font-semibold">Security Note:</strong>{" "}
                  Confirmation emails will be sent to both your old and new
                  email addresses after the change is complete.
                </p>
              </InfoCard>

              <div className="flex flex-col xl:flex-row justify-end gap-3 mt-6">
                <Button
                  onClick={handleBack}
                  variant="secondary"
                  className="w-full xl:w-auto"
                >
                  Back
                </Button>
                <div className="flex flex-col xl:flex-row flex-wrap gap-3 w-full xl:w-auto">
                  <Button
                    onClick={handleClose}
                    variant="secondary"
                    className="w-full xl:w-auto"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={verifyAndChangeMutation.isPending}
                    disabled={newEmailCode.length !== 6}
                    className="w-full xl:w-auto"
                  >
                    Verify & Change Email
                  </Button>
                </div>
              </div>
            </form>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setStep("enter-new-email");
                  setNewEmailCode("");
                }}
                className="text-sm text-electrum hover:text-electrum-400 underline"
              >
                Didn&apos;t receive the code? Try again
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ChangeEmailModal;
